import {
  useEffect,
  useRef,
  useCallback,
  useState,
} from 'react';
import {
  MessageSquare,
  History,
  Plus,
  ChevronLeft,
  Trash2,
} from 'lucide-react';
import { useChat } from './use-chat';
import {
  ChatHeader,
  CategoryBadges,
  EmptyState,
  MessageBubble,
  ToolCallIndicator,
  ChatInput,
} from './chat-components';
import { ConfirmModal } from '../confirm-modal';
import type { Conversation } from './types';

export function ChatPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const {
    messages,
    input,
    isLoading,
    activeCategories,
    currentToolCall,
    conversations,
    currentConversationId,
    isLoadingConversations,
    inputRef,
    abortControllerRef,
    setInput,
    handleSend,
    loadConversation,
    startNewConversation,
    deleteConversation,
  } = useChat();

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, inputRef]);

  const handleClose = () => {
    abortControllerRef.current?.abort();
    setIsOpen(false);
    setShowHistory(false);
    setIsMaximized(false);
  };

  const handleSuggestedQuestion = (question: string) => {
    setInput(question);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSelectConversation = async (conversationId: string) => {
    await loadConversation(conversationId);
    setShowHistory(false);
  };

  const handleNewConversation = () => {
    startNewConversation();
    setShowHistory(false);
  };

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-pink-600 text-white shadow-lg hover:shadow-xl transition-all hover:scale-105"
        aria-label="Open AI Assistant"
      >
        <MessageSquare className="h-6 w-6" />
      </button>
    );
  }

  return (
    <div className={`fixed z-50 flex flex-col bg-background border shadow-2xl transition-all duration-300 ${
      isMaximized 
        ? 'inset-4 rounded-xl' 
        : 'bottom-6 right-6 h-[600px] w-[420px] rounded-2xl'
    }`}>
      <ChatHeader
        onClose={handleClose}
        onHistoryClick={() => setShowHistory(!showHistory)}
        onMaximizeClick={() => setIsMaximized(!isMaximized)}
        isMaximized={isMaximized}
        showHistoryButton
      />

      {showHistory ? (
        <ConversationHistory
          conversations={conversations}
          currentConversationId={currentConversationId}
          isLoading={isLoadingConversations}
          onSelect={handleSelectConversation}
          onNewConversation={handleNewConversation}
          onBack={() => setShowHistory(false)}
          onDelete={deleteConversation}
        />
      ) : (
        <>
          <CategoryBadges categories={activeCategories} />
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              <EmptyState onSuggestedQuestion={handleSuggestedQuestion} />
            ) : (
              messages.map((msg) => <MessageBubble key={msg.id} message={msg} />)
            )}
            {isLoading && currentToolCall && <ToolCallIndicator toolName={currentToolCall} />}
            <div ref={messagesEndRef} />
          </div>
          <ChatInput
            input={input}
            isLoading={isLoading}
            inputRef={inputRef}
            onInputChange={setInput}
            onKeyDown={handleKeyDown}
            onSend={handleSend}
          />
        </>
      )}
    </div>
  );
}

interface ConversationHistoryProps {
  readonly conversations: Conversation[];
  readonly currentConversationId: string | null;
  readonly isLoading: boolean;
  readonly onSelect: (id: string) => void;
  readonly onNewConversation: () => void;
  readonly onBack: () => void;
  readonly onDelete: (id: string) => void;
}

function ConversationHistory({
  conversations,
  currentConversationId,
  isLoading,
  onSelect,
  onNewConversation,
  onBack,
  onDelete,
}: ConversationHistoryProps) {
  return (
    <div className="flex-1 flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </button>
        <button
          type="button"
          onClick={onNewConversation}
          className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-700"
        >
          <Plus className="h-4 w-4" />
          New Chat
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <ConversationListContent
          isLoading={isLoading}
          conversations={conversations}
          currentConversationId={currentConversationId}
          onSelect={onSelect}
          onDelete={onDelete}
        />
      </div>
    </div>
  );
}

interface ConversationListContentProps {
  readonly isLoading: boolean;
  readonly conversations: Conversation[];
  readonly currentConversationId: string | null;
  readonly onSelect: (id: string) => void;
  readonly onDelete: (id: string) => void;
}

function ConversationListContent({
  isLoading,
  conversations,
  currentConversationId,
  onSelect,
  onDelete,
}: ConversationListContentProps) {
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin h-6 w-6 border-2 border-purple-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center px-4">
        <History className="h-12 w-12 text-muted-foreground/50 mb-3" />
        <p className="text-sm text-muted-foreground">No conversations yet</p>
        <p className="text-xs text-muted-foreground mt-1">Start a new chat to begin</p>
      </div>
    );
  }

  const handleDeleteClick = (e: React.MouseEvent, convId: string) => {
    e.stopPropagation();
    setDeleteTarget(convId);
  };

  const handleConfirmDelete = () => {
    if (deleteTarget) {
      onDelete(deleteTarget);
      setDeleteTarget(null);
    }
  };

  return (
    <>
      <div className="divide-y">
        {conversations.map((conv) => (
          <div
            key={conv.id}
            className={`group flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer ${
              conv.id === currentConversationId ? 'bg-purple-50' : ''
            }`}
          >
            <button
              type="button"
              className="flex-1 min-w-0 text-left"
              onClick={() => onSelect(conv.id)}
            >
              <p className="text-sm font-medium truncate">{conv.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {formatRelativeTime(conv.updatedAt)} · {conv.messageCount} messages
              </p>
            </button>
            <button
              type="button"
              onClick={(e) => handleDeleteClick(e, conv.id)}
              className="opacity-0 group-hover:opacity-100 p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded transition-all"
              aria-label="Delete conversation"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Delete Conversation"
        message="Are you sure you want to delete this conversation? This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
